import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';
import { ImageCropper as BackboneImageCropper } from '@bingads-webui/image-cropper';
import { BackboneViewWrapper } from '@bingads-webui-react/backbone-view-wrapper';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getStyle } from './image-cropper-style';

export class UnStyledImageCropper extends React.Component {
  constructor(props) {
    super(props);
    this.previousImageObjSource = this.props.imageSource;
    this.state = {
      isImageLoaded: false,
    };
  }

  componentDidMount() {
    this.loadImageFromSource();
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Make sure we do not rerender and BackboneViewWrapper for unrelated changes
    if (this.state.isImageLoaded === nextState.isImageLoaded &&
      this.props.imageSource === nextProps.imageSource) {
      return false;
    }

    return true;
  }

  componentDidUpdate() {
    this.loadImageFromSource();
  }

  getCropperEdgeOption() {
    if (this.state.isImageLoaded && this.props.maxCropBox) {
      const {
        SourceX, SourceY, SourceWidth, SourceHeight,
      } = this.props.maxCropBox;

      return {
        w: SourceX,
        e: -(this.imageObj.naturalWidth - SourceX - SourceWidth),
        n: SourceY,
        s: -(this.imageObj.naturalHeight - SourceY - SourceHeight),
      };
    }

    return undefined;
  }

  loadImageFromSource() {
    // early return if currently loaded image is the same one
    // not use `this.imageObj.src=== this.props.imageSource`, because Image.src would be converted to absolute path
    // For example,
    // const imageSource = "../../../some.svg";
    // `this.imageObj.src = imageSource;`
    // this.imageObj.src will converted to an absolute path, like "http://localhost:8080/some.svg"
    if (this.imageObj && (this.previousImageObjSource === this.props.imageSource)) {
      return;
    }

    this.setState({ isImageLoaded: false });

    this.imageObj = new Image();
    this.previousImageObjSource = this.props.imageSource;
    this.imageObj.src = this.props.imageSource;
    this.imageObj.onload = () => {
      this.setState({
        isImageLoaded: true,
      });
      this.props.onImageLoaded();
    };
  }

  render() {
    const cropSettings = { ...this.props.cropSettings };
    cropSettings.SourceX = cropSettings.SourceX || 0;
    cropSettings.SourceY = cropSettings.SourceY || 0;
    cropSettings.SourceWidth =
      cropSettings.SourceWidth ||
      (this.imageObj && this.imageObj.width) ||
      0;
    cropSettings.SourceHeight =
      cropSettings.SourceHeight ||
      cropSettings.SourceWidth / this.props.aspectRatio;
    const cropperOptions = {
      aspectRatio: this.props.aspectRatio,
      allowSelect: false,
      canDrag: true,
      boxWidth: this.props.boxWidth,
      boxHeight: this.props.boxHeight,
      minSize: this.props.minSize,
      maxSize: this.props.maxSize,
      edge: this.getCropperEdgeOption(),
      cropSettings,
    };
    const preview = { show: false };
    const imageCropperOptions = {
      imgData: this.imageObj,
      cropperOptions,
      preview,
      isPreviewNeeded: false,
      onCropperInitialized: this.props.onCropperInitialized,
    };

    return (
      <div className={classnames('react-image-cropper', this.props.classes.container)} aria-label={this.props.ariaLabel} >
        { this.state.isImageLoaded &&
          <BackboneViewWrapper
            View={BackboneImageCropper}
            options={imageCropperOptions}
            eventsToActions={[
              {
                event: 'cropSettingsUpdated',
                action: this.props.onCropSettingsChanged,
              },
            ]}
          />
          }
      </div>
    );
  }
}

UnStyledImageCropper.propTypes = {
  aspectRatio: PropTypes.number,
  boxWidth: PropTypes.number,
  boxHeight: PropTypes.number.isRequired,
  minSize: PropTypes.arrayOf(PropTypes.number).isRequired,
  maxSize: PropTypes.arrayOf(PropTypes.number),
  maxCropBox: PropTypes.shape({
    SourceHeight: PropTypes.number.isRequired,
    SourceWidth: PropTypes.number.isRequired,
    SourceY: PropTypes.number.isRequired,
    SourceX: PropTypes.number.isRequired,
  }),
  imageSource: PropTypes.string.isRequired,
  onCropSettingsChanged: PropTypes.func.isRequired,
  onCropperInitialized: PropTypes.func,
  onImageLoaded: PropTypes.func,
  cropSettings: PropTypes.shape({
    SourceHeight: PropTypes.number.isRequired,
    SourceWidth: PropTypes.number.isRequired,
    SourceY: PropTypes.number.isRequired,
    SourceX: PropTypes.number.isRequired,
  }),
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
  }).isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

UnStyledImageCropper.defaultProps = {
  aspectRatio: 1,
  maxSize: [2000, 2592],
  maxCropBox: null,
  onImageLoaded() { },
  cropSettings: null,
  boxWidth: null,
  onCropperInitialized: _.noop,
};

export const ImageCropper = withDefaultStyles(UnStyledImageCropper, getStyle);
